export interface ShareTarget {
  title: string;
  /** Absolute: the share sheet hands it to other apps. */
  url: string;
}

/** How the link left the page: the native share sheet, or the clipboard. */
export type ShareMethod = 'native' | 'copy_link';

export type ShareOutcome =
  | { method: 'native'; status: 'shared' | 'cancelled' }
  | { method: 'copy_link'; status: 'copied' }
  | { method: ShareMethod; status: 'failed' };

/**
 * Opens the device's share sheet, where there is one: phones, and Safari and
 * Chromium on desktop. Elsewhere — Firefox on desktop — the link is copied
 * instead, so the button is never dead.
 *
 * Must be called from the click itself: browsers only open the sheet in
 * response to a user gesture.
 */
export async function shareLink(target: ShareTarget): Promise<ShareOutcome> {
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: target.title, url: target.url });

      return { method: 'native', status: 'shared' };
    } catch (cause) {
      // Dismissing the sheet rejects with AbortError; that is a choice, not a failure.
      const cancelled = cause instanceof DOMException && cause.name === 'AbortError';

      return { method: 'native', status: cancelled ? 'cancelled' : 'failed' };
    }
  }

  try {
    await navigator.clipboard.writeText(target.url);

    return { method: 'copy_link', status: 'copied' };
  } catch {
    return { method: 'copy_link', status: 'failed' };
  }
}

/** Whether `shareLink` will open the native sheet rather than copy. */
export function canShareNatively(): boolean {
  return typeof navigator.share === 'function';
}
